import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import queueService from '../../services/queueService';
import queueDisplayService from '../../services/queueDisplayService';
import { announcementText, groupByStage, newCalls } from '../../utils/queueLogic';
import { clearStoredToken, loadStoredToken, readPairingToken, storeToken } from '../../utils/queueDisplay';
import './QueueDisplayPage.css';

const POLL_MS = 4000;
const MAX_WAITING_SHOWN = 8;

/** Speaks one announcement after another; the browser needs one click on "Sound on" before it will speak. */
const speak = (text) => {
  try {
    if (!window.speechSynthesis || typeof window.SpeechSynthesisUtterance === 'undefined') return;
    const u = new window.SpeechSynthesisUtterance(text);
    u.rate = 0.9;
    window.speechSynthesis.speak(u);
  } catch (e) { /* no speech engine: the screen alone still works */ }
};

/**
 * Full-screen board for a TV in the waiting room. It shows token labels and counter names only; the server never sends a patient name or
 * number to this screen.
 *
 * The TV needs no staff account. It is paired once by opening the link an Administrator or Centre Admin creates in Queue settings: the
 * display token in that link (after the #) is kept on this device and sent to one read-only endpoint. If the token is revoked or expires, the
 * screen says so and stops asking; it never sends anyone to a login page. With `staff`, a logged-in user previews the board of their centre.
 */
export const QueueDisplayPage = ({ staff = false }) => {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('loading');     // loading | ok | unpaired | revoked
  const [sound, setSound] = useState(false);
  const [now, setNow] = useState(new Date());
  const [token, setToken] = useState(() => (staff ? null : loadStoredToken()));
  const previous = useRef(null);
  const soundRef = useRef(false);
  soundRef.current = sound;

  // Pairing: take the token out of the address (it must not stay in the URL or the history) and keep it on this device.
  useEffect(() => {
    if (staff) return;
    const fromLink = readPairingToken(window.location.hash);
    if (!fromLink) { if (!loadStoredToken()) setPhase('unpaired'); return; }
    storeToken(fromLink);
    setToken(fromLink);
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, [staff]);

  const show = useCallback((data) => {
    setError('');
    setPhase('ok');
    const counters = data?.counters || [];
    if (soundRef.current) newCalls(previous.current, counters).forEach((c) => speak(announcementText(c.tokenLabel, c.counterName)));
    previous.current = counters;
    setBoard(data);
  }, []);

  const load = useCallback(async () => {
    try {
      if (staff) {
        const res = await queueService.board();
        if (!res.success) { setError(res.message || 'Could not load the queue.'); return; }
        show(res.data);
        return;
      }
      const res = await queueDisplayService.board(token);
      if (res.unauthorised) { clearStoredToken(); setToken(null); setBoard(null); setPhase('revoked'); return; }
      if (!res.ok) { setError(res.message || 'Could not load the queue.'); return; }
      show(res.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Connection lost. Trying again…');
    }
  }, [staff, token, show]);

  // Polling carries on through a lost connection and stops for good once the token is refused or the screen was never paired.
  const polling = staff || (token && phase !== 'revoked');
  useEffect(() => {
    if (!polling) return undefined;
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [polling, load]);
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 15000); return () => clearInterval(id); }, []);
  useEffect(() => () => { try { window.speechSynthesis?.cancel(); } catch (e) { /* nothing to stop */ } }, []);

  const groups = groupByStage(board?.counters);
  const waitingOf = (stage) => (board?.waiting || []).filter((w) => w.stage === stage);

  if (!staff && (phase === 'unpaired' || phase === 'revoked')) {
    return (
      <div className="qd-screen" role="main">
        <header className="qd-top"><h1>{phase === 'revoked' ? 'This screen is disconnected' : 'This screen is not set up'}</h1></header>
        <p className="qd-empty">
          {phase === 'revoked'
            ? 'Its access was switched off or has expired. Ask the front desk to pair this screen again.'
            : 'Ask the front desk for the display link and open it on this screen.'}
        </p>
      </div>
    );
  }

  return (
    <div className="qd-screen" role="main">
      <header className="qd-top">
        <h1>Please wait for your token</h1>
        <div className="qd-top-right">
          <span className="qd-clock">{now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          <button type="button" className="qd-btn" onClick={() => { setSound((s) => !s); if (!sound) speak('Sound is on'); }} aria-pressed={sound}>
            {sound ? 'Sound on' : 'Sound off'}
          </button>
          {staff && <Link to="/queue" className="qd-btn">Exit</Link>}
        </div>
      </header>

      {error && <div className="qd-error" role="alert">{error}</div>}
      {!board && !error && <p className="qd-empty">Loading…</p>}
      {board && groups.length === 0 && <p className="qd-empty">No counters are set up for this centre.</p>}

      <div className="qd-stages">
        {groups.map((g) => (
          <section key={g.code} className="qd-stage" aria-label={g.label}>
            <h2>{g.label}</h2>
            <div className="qd-counters">
              {g.counters.map((c) => (
                <div key={c.counterId} className={`qd-counter ${c.tokenLabel ? 'busy' : ''}`}>
                  <div className="qd-counter-name">{c.counterName}</div>
                  <div className="qd-token" aria-live="polite">{c.tokenLabel || '—'}</div>
                </div>
              ))}
            </div>
            <div className="qd-next">
              <span>Next:</span>
              {waitingOf(g.code).length === 0 ? <em>no one waiting</em> : waitingOf(g.code).slice(0, MAX_WAITING_SHOWN).map((w) => (
                <span key={w.tokenLabel} className={`qd-chip ${w.priority ? 'priority' : ''}`}>{w.tokenLabel}</span>
              ))}
              {waitingOf(g.code).length > MAX_WAITING_SHOWN && <span className="qd-more">+{waitingOf(g.code).length - MAX_WAITING_SHOWN} more</span>}
            </div>
          </section>
        ))}
      </div>
      <footer className="qd-foot">{board ? `Updated ${new Date(board.serverTime).toLocaleTimeString()}` : ''}</footer>
    </div>
  );
};

export default QueueDisplayPage;
